import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { usePermissions } from './usePermissions';

/**
 * Component to protect routes based on permissions
 * Redirects to unauthorized page if user doesn't have required permission
 * 
 * @param {object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string} [props.requiredPermission] - Single permission required
 * @param {string[]} [props.requiredPermissions] - Array of permissions (requires ALL)
 * @param {string[]} [props.anyPermission] - Array of permissions (requires ANY)
 * @param {string} [props.requiredRole] - Single role required
 * @param {string[]} [props.anyRole] - Array of roles (requires ANY)
 * @param {string} [props.redirectTo] - Path to redirect to if unauthorized (default: '/')
 * @param {React.ReactNode} [props.fallback] - Custom fallback component for unauthorized
 */
export function PermissionProtectedRoute({
  children,
  requiredPermission,
  requiredPermissions,
  anyPermission,
  requiredRole,
  anyRole,
  redirectTo = '/',
  fallback,
}) {
  const { isAuthenticated, isLoading } = useAuth();
  const {
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    hasRole,
    hasAnyRole,
  } = usePermissions();

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/?login=required" replace />;
  }

  // Check permissions
  let hasAccess = true;

  // Check single permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    hasAccess = false;
  }

  // Check multiple permissions (ALL required)
  if (requiredPermissions && !hasAllPermissions(requiredPermissions)) {
    hasAccess = false;
  }

  // Check any permission (ANY required)
  if (anyPermission && !hasAnyPermission(anyPermission)) {
    hasAccess = false;
  }

  // Check single role
  if (requiredRole && !hasRole(requiredRole)) {
    hasAccess = false;
  }

  // Check any role (ANY required)
  if (anyRole && !hasAnyRole(anyRole)) {
    hasAccess = false;
  }

  // If no access, show fallback or redirect
  if (!hasAccess) {
    if (fallback) {
      return fallback;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}

/**
 * Component to show unauthorized page
 */
export function UnauthorizedPage() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center max-w-md p-8">
        <div className="mb-6">
          <svg
            className="mx-auto h-24 w-24 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Access Denied</h1>
        <p className="text-gray-600 mb-6">
          You don't have permission to access this page. Please contact your administrator if you believe this is an error.
        </p>
        <a
          href="/"
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 transition-colors"
        >
          Go to Home
        </a>
      </div>
    </div>
  );
}

export default PermissionProtectedRoute;
